import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, Mail, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Seo from "@/components/common/Seo";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const nav = useNavigate();

  if (localStorage.getItem("admin_token")) return <Navigate to="/adminia" replace />;

  const finish = (data) => {
    localStorage.setItem("admin_token", data.token);
    localStorage.setItem("admin_user", JSON.stringify(data.admin));
    toast.success("Welcome back.");
    nav("/adminia");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      if (data.requires_2fa) {
        setChallenge(data.challenge);
        setCode("");
        toast.message("Enter your 6-digit code");
      } else {
        finish(data);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/login/2fa", { challenge, code: code.trim() });
      finish(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "2FA verification failed";
      toast.error(typeof msg === "string" ? msg : "2FA verification failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title="Studio Login" description="Executive Content Studio admin login." path="/adminia/login" />
      <div className="grid min-h-screen place-items-center bg-navy px-6 py-12 text-white grain-overlay">
        <div className="w-full max-w-md rounded-[1.75rem] border border-white/10 bg-white/[0.03] p-10 backdrop-blur">
          <p className="font-body text-xs font-bold uppercase tracking-[0.3em] text-sky">Executive Insights</p>
          <h1 className="mt-4 font-heading text-3xl font-light leading-tight tracking-tight">
            {challenge ? "Two-factor code." : "Admin sign-in."}
          </h1>
          <p className="mt-3 font-body text-sm text-slate-400">
            {challenge ? "Open your authenticator app and enter the 6-digit code. Or paste a recovery code." : "Sign in to publish and manage articles."}
          </p>

          {!challenge ? (
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <label htmlFor="email" className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Email</label>
                <div className="mt-2 flex items-center rounded-lg border border-white/15 bg-white/5 px-3">
                  <Mail className="h-4 w-4 text-slate-400" />
                  <input id="email" data-testid="admin-login-email" required type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-transparent px-3 py-3 font-body text-sm text-white outline-none placeholder:text-slate-500" placeholder="aftabazad@gmail.com" />
                </div>
              </div>
              <div>
                <label htmlFor="pw" className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Password</label>
                <div className="mt-2 flex items-center rounded-lg border border-white/15 bg-white/5 px-3">
                  <Lock className="h-4 w-4 text-slate-400" />
                  <input id="pw" data-testid="admin-login-password" required type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-transparent px-3 py-3 font-body text-sm text-white outline-none placeholder:text-slate-500" placeholder="••••••••" />
                </div>
              </div>
              <button data-testid="admin-login-submit" disabled={busy} className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-royal px-6 py-3 font-body text-sm font-semibold text-white transition-colors hover:bg-sky hover:text-navy disabled:opacity-60">
                {busy ? "Signing in…" : "Sign in"} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          ) : (
            <form onSubmit={submit2fa} className="mt-8 space-y-5">
              <div>
                <label htmlFor="code" className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Authenticator code</label>
                <div className="mt-2 flex items-center rounded-lg border border-white/15 bg-white/5 px-3">
                  <ShieldCheck className="h-4 w-4 text-slate-400" />
                  <input id="code" data-testid="admin-login-2fa-code" required autoFocus autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} className="w-full bg-transparent px-3 py-3 font-body text-lg tracking-[0.5em] text-white outline-none placeholder:text-slate-600" placeholder="000000" />
                </div>
              </div>
              <button data-testid="admin-login-2fa-submit" disabled={busy || !code.trim()} className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-royal px-6 py-3 font-body text-sm font-semibold text-white transition-colors hover:bg-sky hover:text-navy disabled:opacity-60">
                {busy ? "Verifying…" : "Verify"} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button type="button" onClick={() => { setChallenge(null); setCode(""); }} data-testid="admin-login-2fa-back" className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/10 px-6 py-2 font-body text-xs text-slate-400 hover:border-white/30 hover:text-white">
                Use a different account
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
